import React, { useState, useEffect, useContext, useMemo, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Search, Download, Inbox, X, RotateCcw, Filter, Calendar, ChevronDown, History } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AppContext from "../../context/AppContext";
import Modal from "../../components/modals/Modal";
import {
  BULK_PDF_EXPORT_CODE,
  fetchPdfData,
  getWorkflowInputValueList,
  requestBulkPdfExport,
} from "../../service/Task";
import { useNavigate } from "react-router-dom";
import translations from "../../components/common/Translations";
import ReactSelector from "../../components/common/CommonReactSelector";
import { components } from 'react-select';
import { getActivityListByProjectId } from "../../service/User";
import { getPermittedActivities } from "../../utils/activityPermissions";
import CommonButton from "../../components/common/CommonButton";
import WorklogReportsHeader from "../../components/worklog/WorklogReportsHeader";
import SearchFieldPicker from "../../components/common/SearchFieldPicker";
import datePickerPopper from "../../components/common/DatePickerPopper";
import { isRTL } from "../../utils/rtl";
import { Spinner } from "../../Spinner";
import { toast } from "../../utils/toast";
import { getPermissionByScreenCode } from "../../utils/permissions";
import PageSizeSelector from "../../components/common/PageSizeSelector";
import HoverTooltip from "../../components/common/HoverTooltip";
import useDirection from "../../hooks/useDirection";
import { tableShell } from "../../utils/tableStyles";
import PagerArrowButton from "../../components/common/PagerArrowButton";
import TableScrollShell from "../../components/common/TableScrollShell";
import AlertModal, { useAlertModal } from "../../components/common/AlertModal";

const Placeholder = (props) => {
  return <components.Placeholder {...props} />;
};

const COLUMN_FILTER_POPOVER_WIDTH = 232;

// Portal/fixed-position/outside-click column funnel-filter popover: a
// search-to-narrow checkbox list of distinct values for one table column.
// Duplicated locally, same shape as UserList.js's/Security.js's own copies.
const ColumnFilterPopover = ({ options, selected, onChange, onClose, anchorEl, langMode, loading = false }) => {
  const [search, setSearch] = useState("");
  const [popoverStyle, setPopoverStyle] = useState({});
  const popoverRef = useRef(null);
  const isRtl = isRTL(langMode);

  useLayoutEffect(() => {
    const updatePosition = () => {
      const rect = anchorEl?.getBoundingClientRect();
      if (!rect) return;
      const padding = 8;
      const left = Math.min(
        Math.max(padding, rect.right - COLUMN_FILTER_POPOVER_WIDTH),
        window.innerWidth - COLUMN_FILTER_POPOVER_WIDTH - padding
      );
      setPopoverStyle({ position: "fixed", top: rect.bottom + 6, left, width: COLUMN_FILTER_POPOVER_WIDTH });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorEl]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (anchorEl?.contains(e.target)) return;
      onClose();
    };
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose, anchorEl]);

  const [draft, setDraft] = useState(selected ?? null);
  const selectedSet = draft || new Set(options);
  const filteredOptions = options.filter((opt) =>
    String(opt).toLowerCase().includes(search.toLowerCase())
  );

  const toggleOption = (opt) => {
    const next = new Set(selectedSet);
    if (next.has(opt)) next.delete(opt);
    else next.add(opt);
    setDraft(next.size === options.length ? null : next);
  };

  return createPortal(
    <div
      ref={popoverRef}
      style={popoverStyle}
      className="z-[9999999] bg-white rounded-lg border border-slate-200 shadow-xl p-3 normal-case font-normal tracking-normal"
      onClick={(e) => e.stopPropagation()}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div className="relative mb-2">
        <Search size={12} className={`absolute top-1/2 -translate-y-1/2 text-slate-400 ${isRtl ? "right-2" : "left-2"}`} />
        <input
          type="text"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={translations[langMode].search}
          className={`w-full py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] ${isRtl ? "pr-6 pl-2" : "pl-6 pr-2"}`}
        />
      </div>
      <div className="max-h-48 overflow-y-auto -mx-1 px-1">
        {loading && (
          <p className="text-xs text-slate-400 px-2 py-3 text-center">{translations[langMode]?.loading ?? "Loading..."}</p>
        )}
        {!loading && filteredOptions.length === 0 && (
          <p className="text-xs text-slate-400 px-2 py-3 text-center">{translations[langMode].noDatavb}</p>
        )}
        {!loading && filteredOptions.map((opt) => (
          <label
            key={opt}
            className="flex h-7 items-center gap-2 px-2 rounded hover:bg-slate-50 cursor-pointer text-xs"
          >
            <input
              type="checkbox"
              checked={selectedSet.has(opt)}
              onChange={() => toggleOption(opt)}
              className="accent-[#236DB4] w-3.5 h-3.5 shrink-0"
            />
            <span className="truncate">{opt}</span>
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-100">
        <div className="flex gap-2">
          <button
            type="button"
            className="text-[11px] font-medium text-[#236DB4] hover:underline"
            onClick={() => setDraft(null)}
          >
            {translations[langMode]?.all ?? "All"}
          </button>
          <button
            type="button"
            className="text-[11px] font-medium text-slate-400 hover:underline"
            onClick={() => setDraft(new Set())}
          >
            {translations[langMode]?.none ?? "None"}
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            onChange(draft);
            onClose();
          }}
          className="px-2.5 py-1 text-[11px] font-semibold text-white bg-[#236DB4] rounded hover:bg-[#1d5a96]"
        >
          OK
        </button>
      </div>
    </div>,
    document.body
  );
};

const DATE_RANGE_POPOVER_WIDTH = 300;

const formatShortDate = (date) => {
  if (!date) return "";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// Single button + portaled popover with From/To pickers, matching
// ReportToolbar.js's DateRangeFilterPopover but working directly with Date
// objects instead of ymd strings. Duplicated locally rather than shared.
const DateRangeFilterButton = ({
  startDate,
  endDate,
  onChangeStart,
  onChangeEnd,
  onClear,
  minDate,
  maxDate,
  disabled = false,
  height = "36px",
  langMode,
  isRtl = false,
  theme,
}) => {
  const themeClass = theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "";
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState(null);
  const [draftStart, setDraftStart] = useState(startDate);
  const [draftEnd, setDraftEnd] = useState(endDate);
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const viewportPadding = 8;
      const popoverWidth = Math.min(DATE_RANGE_POPOVER_WIDTH, window.innerWidth - viewportPadding * 2);
      const left = Math.min(
        Math.max(viewportPadding, rect.left),
        window.innerWidth - popoverWidth - viewportPadding
      );
      setPopoverStyle({ top: rect.bottom + 6, left, width: popoverWidth });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      if (buttonRef.current?.contains(event.target)) return;
      if (event.target.closest?.(".react-datepicker, .datepicker-portal")) return;
      setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const hasRange = Boolean(startDate || endDate);

  return (
    <div ref={buttonRef} className={`relative w-full sm:w-auto ${themeClass}`} style={{ height }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!open) {
            setDraftStart(startDate);
            setDraftEnd(endDate);
          }
          setOpen(!open);
        }}
        className={`h-full w-full sm:w-auto px-3 rounded-[10px] border text-sm font-medium flex items-center justify-center gap-2 bg-white whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
          open || hasRange ? "border-[#236DB4] text-[#236DB4]" : "border-gray-300 text-slate-700"
        } ${themeClass}`}
      >
        <Calendar size={14} />
        <span>
          {hasRange
            ? `${formatShortDate(startDate) || "…"} – ${formatShortDate(endDate) || "…"}`
            : translations[langMode]?.dateRange ?? "Date Range"}
        </span>
        <ChevronDown size={14} className={open ? "rotate-180" : ""} />
      </button>

      {open && popoverStyle && createPortal(
        <div
          ref={popoverRef}
          style={{ position: "fixed", top: popoverStyle.top, left: popoverStyle.left, width: popoverStyle.width || DATE_RANGE_POPOVER_WIDTH }}
          className={`z-[80] bg-white rounded-lg border border-slate-200 shadow-xl p-3 normal-case font-normal ${themeClass}`}
          onClick={(e) => e.stopPropagation()}
          dir={isRtl ? "rtl" : "ltr"}
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-500">{translations[langMode].fromdate}</label>
              <DatePicker
                selected={draftStart}
                onChange={(date) => {
                  setDraftStart(date);
                  if (draftEnd && date && draftEnd < date) setDraftEnd(date);
                }}
                placeholderText={translations[langMode].fromdate}
                maxDate={maxDate}
                selectsStart
                startDate={draftStart}
                endDate={draftEnd}
                className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm"
                calendarClassName="!rounded-lg shadow-lg"
                popperPlacement="bottom-start"
                popperContainer={datePickerPopper}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-500">{translations[langMode].todate}</label>
              <DatePicker
                selected={draftEnd}
                onChange={setDraftEnd}
                placeholderText={translations[langMode].todate}
                minDate={minDate ?? draftStart}
                maxDate={maxDate}
                selectsEnd
                startDate={draftStart}
                endDate={draftEnd}
                disabled={!draftStart}
                className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm"
                calendarClassName="!rounded-lg shadow-lg"
                popperPlacement="bottom-start"
                popperContainer={datePickerPopper}
              />
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              onClick={() => {
                onClear?.();
                setOpen(false);
              }}
              className="text-xs font-medium text-slate-500 hover:underline"
            >
              {translations[langMode].reset || "Clear"}
            </button>
            <button
              type="button"
              onClick={() => {
                onChangeStart(draftStart);
                onChangeEnd(draftEnd);
                setOpen(false);
              }}
              className="h-8 px-3 rounded-md text-xs font-semibold text-white bg-[#236DB4] hover:bg-[#1d5a96]"
            >
              {translations[langMode].filter || "Apply"}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const PDF_GENERATOR_FILTERS_KEY = "pdfGeneratorFilters";
const PDF_GENERATOR_FILTERS_SCHEMA_VERSION = 1;
const PDF_SEARCH_SCOPE_VALUES = ["accountNo", "userName"];

const parseYmdToLocalDate = (value) => {
  if (typeof value !== "string") return null;
  const parts = value.split("-");
  if (parts.length !== 3) return null;
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

const readPdfGeneratorFilters = (initialDates) => {
  const fallback = {
    startDate: initialDates.start,
    endDate: initialDates.end,
    inputValue: "",
    searchScope: "accountNo",
    nameQuery: "",
    columnFilters: {},
  };
  try {
    const raw = localStorage.getItem(PDF_GENERATOR_FILTERS_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (parsed?.schemaVersion !== PDF_GENERATOR_FILTERS_SCHEMA_VERSION) return fallback;

    const startDate = parseYmdToLocalDate(parsed?.startDate) ?? fallback.startDate;
    const endDate = parseYmdToLocalDate(parsed?.endDate) ?? fallback.endDate;
    const inputValue = typeof parsed?.inputValue === "string" ? parsed.inputValue : "";
    const searchScope = PDF_SEARCH_SCOPE_VALUES.includes(parsed?.searchScope) ? parsed.searchScope : "accountNo";
    const nameQuery = typeof parsed?.nameQuery === "string" ? parsed.nameQuery : "";

    const columnFilters = {};
    if (parsed?.columnFilters && typeof parsed.columnFilters === "object" && !Array.isArray(parsed.columnFilters)) {
      Object.entries(parsed.columnFilters).forEach(([key, values]) => {
        if (!Array.isArray(values)) return;
        columnFilters[key] = new Set(values.map((v) => String(v)));
      });
    }

    return { startDate, endDate, inputValue, searchScope, nameQuery, columnFilters };
  } catch (error) {
    console.error("Error restoring pdf generator filters:", error);
    return fallback;
  }
};

function PdfGenerator() {
  const initialDates = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    const y = new Date(t);
    y.setDate(t.getDate() - 1);
    return { start: y, end: t };
  }, []);
  const initialFiltersRef = useRef(null);
  if (initialFiltersRef.current === null) {
    initialFiltersRef.current = readPdfGeneratorFilters(initialDates);
  }
  const [pdfData, setPdfData] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [AccountNo, setAccountNo] = useState(() =>
    initialFiltersRef.current.searchScope === "accountNo" ? initialFiltersRef.current.inputValue : ""
  );
  const pdfReportPerm = getPermissionByScreenCode("PDF_REPORT");
  const hasPDFViewAccess = Boolean(pdfReportPerm?.canView);
  const hasPDFCreateAccess = Boolean(pdfReportPerm?.canCreate);
  const hasPDFUpdateAccess = Boolean(pdfReportPerm?.canUpdate);
  const hasPDFDeleteAccess = Boolean(pdfReportPerm?.canDelete);
  const hasPDFDownloadAccess =
    hasPDFCreateAccess || hasPDFUpdateAccess || hasPDFDeleteAccess;
  const formatDateForApi = (date) => {
    if (!date) return "";
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [startDate, setStartDate] = useState(() => initialFiltersRef.current.startDate);
  const [endDate, setEndDate] = useState(() => initialFiltersRef.current.endDate);
  const fromDate = useMemo(() => formatDateForApi(startDate), [startDate]);
  const toDate = useMemo(() => formatDateForApi(endDate), [endDate]);
  const [open, setOpen] = useState(false);
  const [selectedPdfLink, setSelectedPdfLink] = useState("");
  const context = useContext(AppContext);
  const { activityId, updateActivityId, projectId, setWorkflowId, setHeaderActivitySelect } = context;
  const totalPages = Math.ceil(totalValue / pageSize) - 1;
  const startIndex = currentPage * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalValue);
  const totalItems = totalValue;
  const displayStart = totalItems > 0 ? startIndex + 1 : 0;
  const displayEnd = totalItems > 0 ? Math.min(endIndex, totalItems) : 0;
  const { theme, langMode } = useContext(AppContext);
  const { isRTL: isRtl, dir } = useDirection();
  const [hasShownToast, setHasShownToast] = useState(false); // State to track toast display
  const [inputValue, setInputValue] = useState(() => initialFiltersRef.current.inputValue); // Temporary state for input
  const [searchScope, setSearchScope] = useState(() => initialFiltersRef.current.searchScope); // "accountNo" | "userName"
  const [nameQuery, setNameQuery] = useState(() => initialFiltersRef.current.nameQuery);
  const [columnFilters, setColumnFilters] = useState(() => initialFiltersRef.current.columnFilters); // { [colId]: Set<string> }
  const [showColumnFilters, setShowColumnFilters] = useState(
    () => Object.keys(initialFiltersRef.current.columnFilters || {}).length > 0
  );
  const [openFilterPopoverColId, setOpenFilterPopoverColId] = useState(null);
  const filterButtonRefs = useRef({});
  // One-shot flag, true for ~900ms right after showColumnFilters flips
  // false -> true, so the newly-revealed per-column funnel icons can pulse to
  // draw the eye -- same mechanism as TaskListView.js's justRevealedFilters.
  const [justRevealedFilters, setJustRevealedFilters] = useState(false);
  const prevShowColumnFiltersRef = useRef(showColumnFilters);
  useEffect(() => {
    if (showColumnFilters && !prevShowColumnFiltersRef.current) {
      setJustRevealedFilters(true);
      const t = setTimeout(() => setJustRevealedFilters(false), 900);
      prevShowColumnFiltersRef.current = showColumnFilters;
      return () => clearTimeout(t);
    }
    prevShowColumnFiltersRef.current = showColumnFilters;
  }, [showColumnFilters]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(false)
  const [selectedAccountNos, setSelectedAccountNos] = useState(() => new Set());
  const [workflowInputValueOptions, setWorkflowInputValueOptions] = useState([]);
  const [selectedWorkflowInputValue, setSelectedWorkflowInputValue] = useState(null);
  const [inputValuesReady, setInputValuesReady] = useState(false);
  const { alertProps, showAlert } = useAlertModal();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      try {
        localStorage.setItem(
          PDF_GENERATOR_FILTERS_KEY,
          JSON.stringify({
            schemaVersion: PDF_GENERATOR_FILTERS_SCHEMA_VERSION,
            startDate: formatDateForApi(startDate),
            endDate: formatDateForApi(endDate),
            inputValue,
            searchScope,
            nameQuery,
            columnFilters: Object.fromEntries(
              Object.entries(columnFilters).map(([key, values]) => [key, Array.from(values)])
            ),
          })
        );
      } catch (error) {
        console.error("Error saving pdf generator filters:", error);
      }
    }, 150);
    return () => window.clearTimeout(timer);
  }, [startDate, endDate, inputValue, searchScope, nameQuery, columnFilters]);

  const singleWorkflowId = useMemo(() => {
    if (!activityId) return null;
    const ids = String(activityId)
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
    return ids.length === 1 ? ids[0] : null;
  }, [activityId]);

  const getData = async (accountNoOverride = AccountNo) => {
    if (!hasPDFViewAccess) return;
    setLoading(true)
    try {
      const data = await fetchPdfData(
        singleWorkflowId ?? activityId,
        accountNoOverride,
        currentPage,
        pageSize,
        fromDate,
        toDate,
        selectedWorkflowInputValue?.value ?? null
      );
      setPdfData(Array.isArray(data?.taskPdfList) ? data.taskPdfList : []);
      setTotalValue(data?.count || 0);
      setLoading(false)
      setSelectedAccountNos(new Set());
    } catch (error) {
      console.log(error)
      setLoading(false)
    }
  };
  useEffect(() => {
    if (!hasPDFViewAccess) return;
    if (!activityId) {
      if (!hasShownToast) {
        setHasShownToast(true);
      }
      return;
    }
    if (!inputValuesReady) return;
    if (
      singleWorkflowId &&
      workflowInputValueOptions.length > 0 &&
      !selectedWorkflowInputValue?.value
    ) {
      setPdfData([]);
      setTotalValue(0);
      return;
    }
    setHasShownToast(false);
    getData();
  }, [
    activityId,
    AccountNo,
    currentPage,
    pageSize,
    fromDate,
    toDate,
    hasPDFViewAccess,
    selectedWorkflowInputValue,
    workflowInputValueOptions,
    inputValuesReady,
    singleWorkflowId,
  ]);

  const handlePageSizeChange = (size) => {
    if (!hasPDFViewAccess) return;
    setPageSize(size);
    setCurrentPage(0);
  };

  const goToPreviousPage = () => {
    if (!hasPDFViewAccess) return;
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (!hasPDFViewAccess) return;
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };


  const handleSearch = () => {
    if (!hasPDFViewAccess) return;
    const next = inputValue;
    setCurrentPage(0);
    setAccountNo(next); // Set the final value on search
    getData(next);
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInputValue(value);

    if (value === "") {
      setAccountNo(value);
    }
  };

  const handleSearchScopeChange = (nextScope) => {
    if (nextScope === searchScope) return;
    setCurrentPage(0);
    if (nextScope === "accountNo") {
      setNameQuery("");
    } else {
      setInputValue("");
      setAccountNo("");
    }
    setSearchScope(nextScope);
  };

  useEffect(() => {
    if (!hasPDFViewAccess) return;
    if (searchScope !== "accountNo") return;
    if (inputValue === AccountNo) return;
    const handle = setTimeout(() => {
      setCurrentPage(0);
      setAccountNo(inputValue);
      getData(inputValue);
    }, 400);
    return () => clearTimeout(handle);
  }, [inputValue, hasPDFViewAccess, searchScope]);

  const handleExportClick = (pdfLink) => {
    if (!hasPDFDownloadAccess) return;
    setSelectedPdfLink(pdfLink);
    setOpen(true);
  };

  // "Reset" clears every page-local filter (date range, radio/workflow-input
  // select, Account No/User Name search, column funnel-filters) but
  // deliberately leaves the Activity dropdown alone -- it reads/writes the
  // shared AppContext activityId, used by other pages too, not a page-local
  // filter.
  const isDateRangeChanged =
    formatDateForApi(startDate) !== formatDateForApi(initialDates.start) ||
    formatDateForApi(endDate) !== formatDateForApi(initialDates.end);
  const isSearchActive = Boolean(inputValue) || Boolean(nameQuery) || searchScope !== "accountNo";
  const activeFilterCount =
    Object.keys(columnFilters).length +
    (selectedWorkflowInputValue ? 1 : 0) +
    (isSearchActive ? 1 : 0) +
    (isDateRangeChanged ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0;
  const hasActiveColumnFilters = Object.keys(columnFilters).length > 0;

  const handleResetAllFilters = () => {
    setColumnFilters({});
    setSelectedWorkflowInputValue(null);
    setSearchScope("accountNo");
    setNameQuery("");
    setInputValue("");
    setAccountNo("");
    setStartDate(initialDates.start);
    setEndDate(initialDates.end);
    setCurrentPage(0);
  };

  const formatVisitDateValue = (visitDate) =>
    visitDate
      ? new Date(visitDate).toLocaleDateString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit" })
      : "Invalid date";

  const formatVisitTimeValue = (visitTime) =>
    visitTime
      ? new Date(`1970-01-01T${visitTime}`).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
      : "Invalid time";

  const FILTERABLE_COLUMNS = [
    { id: "accountNo", label: translations[langMode].accno, getValue: (item) => item.accountNo || "" },
    { id: "visitDate", label: translations[langMode].visitDate, getValue: (item) => formatVisitDateValue(item.visitDate) },
    { id: "visitTime", label: translations[langMode].visitTime, getValue: (item) => formatVisitTimeValue(item.visitTime) },
    { id: "userName", label: translations[langMode].username, getValue: (item) => item.name || "-" },
  ];

  const filterOptionsByCol = useMemo(() => {
    const map = {};
    FILTERABLE_COLUMNS.forEach((col) => {
      map[col.id] = Array.from(new Set((pdfData || []).map((item) => col.getValue(item)).filter(Boolean)));
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfData, langMode]);

  const displayedPdfData = useMemo(() => {
    let rows = pdfData || [];
    if (searchScope === "userName" && nameQuery.trim()) {
      const q = nameQuery.trim().toLowerCase();
      rows = rows.filter((item) => (item.name || "").toLowerCase().includes(q));
    }
    FILTERABLE_COLUMNS.forEach((col) => {
      const selected = columnFilters[col.id];
      if (selected) rows = rows.filter((item) => selected.has(col.getValue(item)));
    });
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfData, searchScope, nameQuery, columnFilters, langMode]);

  const pageAccountNos = useMemo(() => {
    return (displayedPdfData || []).map((x) => x?.accountNo).filter(Boolean);
  }, [displayedPdfData]);

  const isAllPageSelected = useMemo(() => {
    if (pageAccountNos.length === 0) return false;
    return pageAccountNos.every((acc) => selectedAccountNos.has(acc));
  }, [pageAccountNos, selectedAccountNos]);

  const toggleSelectAllOnPage = () => {
    if (!hasPDFDownloadAccess) return;
    setSelectedAccountNos((prev) => {
      const next = new Set(prev);
      const shouldSelectAll = !isAllPageSelected;
      pageAccountNos.forEach((acc) => {
        if (shouldSelectAll) next.add(acc);
        else next.delete(acc);
      });
      return next;
    });
  };

  const toggleRowSelection = (accountNo) => {
    if (!hasPDFDownloadAccess || !accountNo) return;
    setSelectedAccountNos((prev) => {
      const next = new Set(prev);
      if (next.has(accountNo)) next.delete(accountNo);
      else next.add(accountNo);
      return next;
    });
  };

  // Up-front warnings about the gap between what the table is showing and what
  // the export request can actually express: the bulk endpoint takes only
  // workflowId + date range + accountNos + radioId, so the client-side
  // column/user-name filters don't narrow an "export all".
  const bulkExportNotices = useMemo(() => {
    if (selectedAccountNos.size > 0) return [];
    const notices = [];
    if (Object.keys(columnFilters).length > 0 || (searchScope === "userName" && nameQuery.trim())) {
      notices.push(
        translations[langMode]?.bulkExportTableFilterNotice ??
          "Column and user-name filters only narrow the table on this page, not the export. Tick specific rows to export only those."
      );
    }
    return notices;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountNos, columnFilters, searchScope, nameQuery, langMode]);

  const navigate = useNavigate();
  const handleExportHistoryClick = () => navigate("/pdf-export-history");

  // The zip is prepared by a background job (BLF-2171): this queues the export
  // and hands off to the history screen, which polls the status endpoint for
  // progress. The old download-task-pdf-list call streamed the whole archive
  // onto one response and froze the browser on large selections.
  // Anything that stops an export from being created is raised as a blocking
  // alert the user has to acknowledge -- a toast is too easy to miss when the
  // fix is "go change a filter". Only the queued-successfully message and the
  // advisory notices below stay as toasts.
  const handleDownloadPdfs = async () => {
    if (!hasPDFDownloadAccess) return;
    if (!activityId) {
      showAlert({
        variant: "warning",
        title: translations[langMode]?.bulkExportCheckSelectionTitle ?? "Check your selection",
        message:
          translations[langMode]?.invalidworkflow ?? "Please select an activity/workflow.",
      });
      return;
    }
    // The export endpoint takes exactly one workflowId, so a multi-activity
    // selection has to be narrowed before anything can be queued.
    if (!singleWorkflowId) {
      showAlert({
        variant: "warning",
        title: translations[langMode]?.bulkExportCheckSelectionTitle ?? "Check your selection",
        message:
          translations[langMode]?.bulkExportSingleActivityWarning ??
          "Select a single activity before exporting PDFs.",
      });
      return;
    }
    if (!fromDate || !toDate) {
      showAlert({
        variant: "warning",
        title: translations[langMode]?.bulkExportCheckSelectionTitle ?? "Check your selection",
        message:
          translations[langMode]?.bulkExportDateRangeWarning ??
          "Select a date range before exporting PDFs.",
      });
      return;
    }

    setLoading(true);
    try {
      const { code, message, value } = await requestBulkPdfExport({
        workflowId: singleWorkflowId,
        fromDate,
        toDate,
        accountNos: Array.from(selectedAccountNos),
        radioId: selectedWorkflowInputValue?.value ?? null,
      });

      // 601/607 bodies are finished user-facing sentences naming the actual
      // server-side limit and the suggested alternative -- shown as-is. All
      // four 601 cases (date range too wide, too many account numbers, too
      // many matched PDFs, too many exports already running) land here, so
      // nothing is rewritten or paraphrased and no limit is hardcoded.
      if (code === BULK_PDF_EXPORT_CODE.LIMIT) {
        showAlert({
          variant: "warning",
          title: translations[langMode]?.bulkExportLimitHeading ?? "Export could not be started",
          message:
            (typeof value === "string" ? value : message) ||
            (translations[langMode]?.bulkExportLimitFallback ??
              "This export could not be started. Please narrow your selection and try again."),
        });
        return;
      }
      if (code === BULK_PDF_EXPORT_CODE.NO_DATA) {
        showAlert({
          variant: "info",
          title:
            translations[langMode]?.bulkExportEmptyHeading ??
            "Nothing matched this selection",
          message:
            typeof value === "string"
              ? value
              : translations[langMode]?.bulkExportEmptyHelp ??
                "Try a different date range, activity, or account selection.",
        });
        return;
      }
      if (code !== BULK_PDF_EXPORT_CODE.SUCCESS || !value?.exportId) {
        showAlert({
          variant: "error",
          title: translations[langMode]?.bulkExportFailedHeading ?? "Export failed",
          message:
            (typeof value === "string" ? value : message) ||
            (translations[langMode]?.bulkExportFailedHelp ??
              "The export could not be completed. Please try again."),
        });
        return;
      }

      bulkExportNotices.forEach((notice) => toast.warning(notice, { autoClose: 12000 }));
      if (value.message) toast.success(value.message);
      navigate(`/pdf-export-history?exportId=${value.exportId}`);
    } catch (error) {
      console.error("Error requesting bulk PDF export:", error);
      showAlert({
        variant: "error",
        title: translations[langMode]?.bulkExportFailedHeading ?? "Export failed",
        message:
          error?.response?.data?.body?.value ??
          translations[langMode]?.bulkExportRequestError ??
          "Could not start the export. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };
  const handleScheduleTabClick = () => {
    navigate("/worklog");
  };

  const handleExportTabClick = () => {
    navigate("/pdf-generator");
  };

  // Enabled only once the table actually has fetched rows to export -- with no
  // activity, an in-flight fetch or an empty result there is nothing to queue.
  const canQueueBulkExport =
    hasPDFDownloadAccess && Boolean(activityId) && !loading && pdfData.length > 0;

  const headerDownloadAction = (
    <div className="flex w-full flex-wrap items-center gap-2.5 sm:w-auto sm:gap-3">
      <HoverTooltip
        text={translations[langMode]?.dateRangeTooltip ?? "Filter by date range"}
        className="relative block min-w-0 w-full sm:w-auto"
      >
        <DateRangeFilterButton
          startDate={startDate}
          endDate={endDate}
          onChangeStart={(date) => {
            setStartDate(date);
            if (endDate && date && endDate < date) {
              setEndDate(date);
            }
          }}
          onChangeEnd={(date) => setEndDate(date)}
          onClear={() => {
            setStartDate(initialDates.start);
            setEndDate(initialDates.end);
          }}
          maxDate={today}
          disabled={!hasPDFViewAccess}
          height="40px"
          langMode={langMode}
          isRtl={isRtl}
          theme={theme}
        />
      </HoverTooltip>
      {singleWorkflowId && (
        <HoverTooltip
          text={translations[langMode]?.workflowInputTooltip ?? "Filter by additional criteria"}
          className="relative flex min-w-0 w-full flex-col gap-1 sm:w-56 sm:flex-none sm:shrink-0"
        >
          <ReactSelector
            value={selectedWorkflowInputValue}
            options={workflowInputValueOptions}
            onChange={(opt) => {
              setSelectedWorkflowInputValue(opt);
              setCurrentPage(0);
            }}
            height="40px"
            menuPlacement="bottom"
            isDisabled={!singleWorkflowId || !hasPDFViewAccess}
            isClearable
            placeholder={translations[langMode]?.selectRadioOption ?? "Select radio option"}
          />
        </HoverTooltip>
      )}
      <div className="flex w-full items-center gap-2 sm:contents">
        <CommonButton colorIntent="primary"
          variant="primary"
          type="button"
          onClick={handleDownloadPdfs}
          disabled={!canQueueBulkExport}
          tooltipText={
            selectedAccountNos.size > 0
              ? translations[langMode]?.downloadSelectedPdfsTooltip ?? "Download selected PDFs"
              : translations[langMode]?.downloadAllPdfsTooltip ?? "Download all PDFs"
          }
          tooltipPosition="top"
          className="h-10 min-h-[40px] min-w-0 flex-1 text-xs sm:w-auto sm:flex-none sm:text-sm font-medium inline-flex items-center justify-center gap-2 shadow-sm whitespace-nowrap shrink-0 sm:min-w-[150px]"
        >
          <Download size={16} className="shrink-0" />
          <span className="truncate">
            {selectedAccountNos.size > 0
              ? translations[langMode]?.downloadSelected ?? "Download Selected"
              : translations[langMode]?.downloadAll ?? "Download All"}
          </span>
        </CommonButton>
        <button
          data-table-control="filters"
          aria-pressed={showColumnFilters}
          type="button"
          onClick={() => setShowColumnFilters((v) => !v)}
          className={`flex h-10 min-h-[40px] min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border px-2 text-xs font-medium sm:hidden ${
            showColumnFilters || hasActiveColumnFilters
              ? "bg-blue-50 border-[#236DB4] text-[#236DB4]"
              : "bg-white border-slate-200 text-slate-600"
          }`}
        >
          <Filter size={14} className="shrink-0" />
          <span className="truncate">{translations[langMode].columnFiltersLabel}</span>
        </button>
      </div>
    </div>
  );


  const handleChangeActivity = (selectedValue) => {
    if (!hasPDFViewAccess) return;
    const workflowId = selectedValue?.value ?? "";
    updateActivityId(workflowId);
    setSelectedWorkflowInputValue(null);
    setCurrentPage(0);
    if (workflowId) {
      localStorage.setItem("activityId", workflowId);
    }
  };

  useEffect(() => {
    const fetchActivityList = async () => {
      try {
        const data = await getActivityListByProjectId(projectId);
        setActivity(data);
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };
    if (projectId) {
      fetchActivityList();
    }
  }, [projectId]);


  const permittedActivity = useMemo(() => {
    return getPermittedActivities(activity);
  }, [activity]);

  const option = useMemo(() => {
    return (permittedActivity || [])
      .filter(Boolean)
      .map((item) => ({
        value: item.workflowId,
        label: item.workflowName,
      }));
  }, [permittedActivity]);

  const searchFieldOptions = useMemo(() => ([
    { value: "accountNo", label: translations[langMode].accno },
    { value: "userName", label: translations[langMode].username },
  ]), [langMode]);

  const defaultActivityValue = useMemo(() => {
    if (projectId !== null && activityId && permittedActivity?.length) {
      const activityIdList = typeof activityId === 'string'
        ? activityId.split(',')
        : Array.isArray(activityId)
          ? activityId.map(id => id.toString())
          : [activityId?.toString()];

      return (permittedActivity || [])
        .filter((org) => activityIdList.includes(org.workflowId?.toString()))
        .map((dept) => ({
          value: dept.workflowId,
          label: dept.workflowName,
        }));
    }
    return [];
  }, [projectId, activityId, permittedActivity]);

  useEffect(() => {
    if (defaultActivityValue && defaultActivityValue.length > 0) {
      const valueString = defaultActivityValue.map(item => item.value).join(',');
      setWorkflowId(valueString);
      updateActivityId(valueString);
      localStorage.setItem("activityId", valueString);
    }
  }, [defaultActivityValue]);

  const handleChangeActivityRef = useRef(handleChangeActivity);
  handleChangeActivityRef.current = handleChangeActivity;

  // Activity picker lives in the top header slot, same as the Task List page.
  useEffect(() => {
    if (!setHeaderActivitySelect) return undefined;

    setHeaderActivitySelect(
      <ReactSelector
        options={option || []}
        onChange={(value) => handleChangeActivityRef.current(value)}
        components={Placeholder}
        placeholder={translations[langMode].Activities}
        isClearable={false}
        isDisabled={!hasPDFViewAccess}
        height="42px"
        menuPortalTarget={document.body}
        value={defaultActivityValue}
      />
    );

    return () => setHeaderActivitySelect(null);
  }, [setHeaderActivitySelect, option, defaultActivityValue, hasPDFViewAccess, langMode]);

  useEffect(() => {
    const loadWorkflowInputValues = async () => {
      setInputValuesReady(false);
      // Endpoint supports a single workflowId; if multi-selected, we disable/clear dropdown.
      if (!singleWorkflowId) {
        setWorkflowInputValueOptions([]);
        setSelectedWorkflowInputValue(null);
        setInputValuesReady(true);
        return;
      }

      const list = await getWorkflowInputValueList(singleWorkflowId);
      const getFirstValue = (source, keys) =>
        keys.map((key) => source?.[key]).find((value) => value != null && String(value).trim() !== "");

      const buildOptionLabel = (source, fallbackValue) => {
        const question = getFirstValue(source, [
          "question",
          "questionName",
          "fieldQuestion",
          "fieldQuestionName",
          "fieldLabel",
          "fieldName",
        ]);
        const option = getFirstValue(source, [
          "option",
          "optionName",
          "optionValue",
          "inputValue",
          "name",
          "label",
          "value",
        ]);

        if (question && option && String(question) !== String(option)) {
          return `${question}: ${option}`;
        }

        return String(question || option || fallbackValue || "");
      };

      const normalized = list
        .map((x) => {
          if (x == null) return null;

          if (typeof x === "string" || typeof x === "number") {
            return { value: String(x), label: String(x) };
          }

          const radioId =
            x.radioId != null && String(x.radioId).trim() !== ""
              ? String(x.radioId).trim()
              : "";

          if (radioId) {
            return {
              value: radioId,
              label: buildOptionLabel(x, radioId),
            };
          }

          const v =
            x.value ??
            x.inputValue ??
            x.name ??
            x.label ??
            x.workflowInputValue ??
            x.workflowInputValueName ??
            x.id ??
            "";

          if (v === "" || v == null) return null;

          return {
            value: String(v),
            label: buildOptionLabel(x, v),
          };
        })
        .filter(Boolean);
      setWorkflowInputValueOptions(normalized);
      setSelectedWorkflowInputValue(null);
      setInputValuesReady(true);
    };

    loadWorkflowInputValues();
  }, [singleWorkflowId]);

  return (
    <div className="flex min-h-full sm:h-full sm:min-h-0 w-full min-w-0 flex-col overflow-y-auto sm:overflow-hidden bg-white" dir={dir}>
      <div
        className={`box-border flex min-h-full sm:h-full sm:min-h-0 w-full min-w-0 flex-col bg-white ${theme === "dark"
            ? "dark-mode  "
            : theme === "high-contrast"
              ? "high-contrast  "
              : ""
          }`}
      >
        <div className="flex min-h-full sm:h-full sm:min-h-0 min-w-0 w-full flex-col">
          {loading && <Spinner />}

          <div
            className={`mx-2 mt-2 min-w-0 shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm flex flex-col gap-3 sm:mx-4 sm:mt-3 sm:px-5 sm:py-4 sm:gap-4 ${theme === "dark"
              ? "dark-mode"
              : theme === "high-contrast"
                ? "high-contrast"
                : ""
              }`}
          >
          <WorklogReportsHeader
            title={translations[langMode].pdf}
            subtitle={translations[langMode]?.pdfReportsSubtitle ?? "Search, filter, and download visit PDF reports for a date range."}
            activeTab="export"
            onScheduleClick={handleScheduleTabClick}
            onExportClick={handleExportTabClick}
            hasPDFAccess={hasPDFViewAccess}
            showTabSwitcher={false}
            stackActionsUntilDesktop
            extraActions={headerDownloadAction}
            theme={theme}
            langMode={langMode}
            isRtl={isRtl}
          />
          {/* Wraps at every width instead of being forced onto one line from
              lg (1214px) up: with activity, search, Export History, Reset
              and Filters all in this row, a forced single line clipped the
              trailing buttons on laptops. */}
          <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
            <div className="flex w-full max-w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-1 lg:contents">
              <HoverTooltip
                text={translations[langMode]?.filtersToggleTooltip ?? "Apply filters from the table header"}
                className="relative hidden shrink-0 sm:inline-flex"
              >
                <button data-table-control="filters" aria-pressed={showColumnFilters}
                  type="button"
                  onClick={() => setShowColumnFilters((v) => !v)}
                  className={`w-auto px-3 py-2 rounded-lg border text-sm font-medium flex items-center justify-center gap-1.5 shrink-0 whitespace-nowrap ${showColumnFilters || hasActiveColumnFilters
                    ? "bg-blue-50 border-[#236DB4] text-[#236DB4]"
                    : "bg-white border-slate-200 text-slate-600"
                    }`}
                >
                  <Filter size={14} /> {translations[langMode].columnFiltersLabel}
                </button>
              </HoverTooltip>
              <HoverTooltip
                text={
                  searchScope === "accountNo"
                    ? translations[langMode]?.searchByAccountNumber ?? "Search by account number"
                    : translations[langMode]?.username ?? "Search by user name"
                }
                className="relative flex h-9 min-w-0 w-full max-w-full sm:w-[320px] sm:flex-none lg:w-[320px] lg:flex-none lg:max-w-full"
              >
                <SearchFieldPicker
                  options={searchFieldOptions}
                  activeValue={searchScope}
                  onSelect={handleSearchScopeChange}
                  searchPlaceholder={translations[langMode]?.findAField ?? "Find a field..."}
                  isRtl={isRtl}
                  buttonTestId="pdf-search-scope-button"
                />
                <div className="relative min-w-0 max-w-full flex-1">
                  <Search
                    size={14}
                    className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-slate-400 ${isRtl ? "right-2.5" : "left-2.5"}`}
                  />
                  <input
                    id="account-search"
                    autoComplete="new-password"
                    type="text"
                    disabled={!hasPDFViewAccess}
                    className={`w-full min-w-0 h-9 text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] disabled:opacity-60 ${isRtl ? "pr-8 pl-7 rounded-l-lg border-r-0 text-right" : "pl-8 pr-7 rounded-r-lg border-l-0 text-left"} ${
                      theme === "dark"
                        ? "dark-mode"
                        : theme === "high-contrast"
                          ? "high-contrast"
                          : ""
                    }`}
                    placeholder={`${translations[langMode]?.search ?? "Search"} ${searchScope === "accountNo" ? translations[langMode].accno : translations[langMode].username}...`}
                    value={searchScope === "accountNo" ? inputValue : nameQuery}
                    onChange={(e) => (searchScope === "accountNo" ? handleInputChange(e) : setNameQuery(e.target.value))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && searchScope === "accountNo") handleSearch();
                    }}
                  />
                  {(searchScope === "accountNo" ? inputValue : nameQuery) && (
                    <button
                      type="button"
                      onClick={() =>
                        searchScope === "accountNo"
                          ? handleInputChange({ target: { value: "" } })
                          : setNameQuery("")
                      }
                      className={`absolute top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 ${isRtl ? "left-2" : "right-2"}`}
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              </HoverTooltip>
            </div>
            <div className="flex w-full items-center justify-between sm:justify-end gap-2 sm:w-auto lg:contents">
              <HoverTooltip
                text={translations[langMode]?.bulkExportHistoryTooltip ?? "View previous bulk PDF exports"}
                className="relative flex-1 sm:flex-initial inline-flex lg:ml-auto"
              >
                <button
                  type="button"
                  onClick={handleExportHistoryClick}
                  disabled={!hasPDFViewAccess}
                  aria-label={translations[langMode]?.bulkExportHistoryTitle ?? "Export History"}
                  className="w-full sm:w-auto h-9 px-3 lg:px-2.5 2xl:px-3 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 flex items-center justify-center gap-1.5 shrink-0 disabled:opacity-40 hover:bg-slate-50"
                >
                  <History size={13} className="shrink-0" />
                  <span className="truncate inline">
                    {translations[langMode]?.bulkExportHistoryTitle ?? "Export History"}
                  </span>
                </button>
              </HoverTooltip>
              <HoverTooltip
                text={translations[langMode]?.resetAllAppliedFiltersTooltip ?? "Clear all applied filters"}
                className="relative flex-1 sm:flex-initial inline-flex"
              >
                <div className="relative w-full sm:w-auto shrink-0">
                  <button data-button-intent="warning"
                    type="button"
                    onClick={handleResetAllFilters}
                    disabled={!hasActiveFilters}
                    className="w-full sm:w-auto h-9 px-3 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 flex items-center justify-center gap-1.5 disabled:opacity-40 hover:bg-slate-50"
                  >
                    <RotateCcw size={13} /> <span>{translations[langMode].reset}</span>
                  </button>
                  {activeFilterCount > 0 && (
                    <span
                      className={`absolute -top-2 ${isRtl ? "-left-2" : "-right-2"} grid min-w-[18px] h-[18px] px-1 place-items-center rounded-full bg-[#ff3b30] text-white text-[10px] font-semibold leading-none pointer-events-none`}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </div>
              </HoverTooltip>
            </div>
          </div>
          </div>

          <div className="flex-1 min-h-0 w-full flex flex-col">
            <div
              className={`mx-2 mb-2 mt-2 flex min-h-[22rem] min-w-0 flex-none flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:mx-4 sm:mb-3 sm:mt-3 sm:min-h-0 sm:flex-1 ${theme === "dark"
                ? "dark-mode"
                : theme === "high-contrast"
                  ? "high-contrast"
                  : ""
                }`}
            >
              <div className={`scrollbar-container relative z-20 overflow-visible flex flex-wrap items-center justify-between px-3 py-2.5 border-b border-slate-100 bg-white sm:px-5 ${theme === "dark" ? "border-gray-600" : ""}`}>
                <div className="flex w-full items-center justify-between gap-2 sm:mx-auto sm:mt-1 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="flex items-center gap-3 text-xs text-slate-500 sm:me-2 sm:gap-4 sm:whitespace-nowrap">
                      <span className="rtl-keep-ltr inline sm:hidden">{`${displayStart}-${displayEnd} / ${totalItems}`}</span>
                      <span className="hidden sm:inline">
                        {(translations[langMode]?.bulkExportShowingRange ?? "Showing {start}-{end} of {total}")
                          .replace("{start}", displayStart)
                          .replace("{end}", displayEnd)
                          .replace("{total}", totalItems)}
                      </span>
                    </p>
                  </div>
                  <div className="flex min-w-0 items-center gap-2 text-xs text-slate-500 sm:ml-auto sm:gap-3">
                    <div className="flex items-center gap-1.5">
                      <label htmlFor="pageSize" className="hidden max-w-[96px] leading-tight sm:block sm:max-w-none sm:whitespace-nowrap">{translations[langMode].rows}</label>
                      <PageSizeSelector
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        disabled={!hasPDFViewAccess}
                        height="32px"
                        fontSize="12px"
                      />
                    </div>
                    <span className="pager-nav flex items-center gap-1">
                      <PagerArrowButton
                        direction="prev"
                        isRtl={isRtl}
                        onClick={goToPreviousPage}
                        disabled={!hasPDFViewAccess || currentPage === 0}
                        ariaLabel={translations[langMode]?.previousPage || "Previous page"}
                      />
                      <span className="text-xs text-slate-600 px-1 whitespace-nowrap">{currentPage + 1} / {Math.max(totalPages + 1, 1)}</span>
                      <PagerArrowButton
                        direction="next"
                        isRtl={isRtl}
                        onClick={goToNextPage}
                        disabled={!hasPDFViewAccess || currentPage === totalPages}
                        ariaLabel={translations[langMode]?.nextPage || "Next page"}
                      />
                    </span>
                  </div>
                </div>
              </div>
              <TableScrollShell className="w-full flex-1 min-h-0">
              <table
                className={`min-w-[760px] w-full text-xs text-left rtl:text-right text-gray-500 dark:text-gray-400 ${theme === "dark" ? "dark-mode" : ""}`}
                dir={dir}
              >
                <thead
                  className={`sticky top-0 z-10 text-xs text-slate-700 border-b border-slate-200 ${theme === "dark"
                    ? "bg-gray-800 text-gray-200 border-gray-700"
                    : "bg-slate-50"
                    }`}
                >
                  <tr>
                    {hasPDFDownloadAccess ? (
                      <th className="w-[60px] min-w-[60px] max-w-[60px] px-0 py-3.5 text-center">
                        <input
                          type="checkbox"
                          checked={isAllPageSelected}
                          onChange={toggleSelectAllOnPage}
                          className={tableShell.checkbox}
                          aria-label={translations[langMode]?.selectAll ?? "Select all rows"}
                        />
                      </th>
                    ) : null}
                    <th className="w-[60px] min-w-[60px] max-w-[60px] text-xs font-semibold uppercase py-3.5 px-0 text-slate-400 tracking-[0.04em] text-center">
                      {translations[langMode]?.number ?? "NO."}
                    </th>
                    {FILTERABLE_COLUMNS.map((col) => (
                      <th
                        key={col.id}
                        className="relative px-2 py-3.5 text-xs font-semibold uppercase text-slate-500 tracking-[0.04em] text-left rtl:text-right whitespace-nowrap"
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {showColumnFilters && (
                            <span className={justRevealedFilters ? "inline-flex animate-filter-pulse" : "inline-flex"}>
                              <button
                                type="button"
                                ref={(el) => { filterButtonRefs.current[col.id] = el; }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenFilterPopoverColId((prev) => (prev === col.id ? null : col.id));
                                }}
                                className={`inline-flex items-center justify-center w-5 h-5 rounded shrink-0 transition-colors ${columnFilters[col.id] ? "text-[#236DB4] bg-blue-50" : "text-[#236DB4] hover:bg-blue-50"}`}
                                aria-label={`${translations[langMode].filters} ${col.label}`}
                              >
                                <Filter size={12} fill={columnFilters[col.id] ? "currentColor" : "none"} />
                              </button>
                            </span>
                          )}
                        </span>
                        {showColumnFilters && openFilterPopoverColId === col.id && (
                          <ColumnFilterPopover
                            options={filterOptionsByCol[col.id] || []}
                            selected={columnFilters[col.id] ?? null}
                            onChange={(next) =>
                              setColumnFilters((prev) => {
                                const copy = { ...prev };
                                if (next === null) delete copy[col.id];
                                else copy[col.id] = next;
                                return copy;
                              })
                            }
                            onClose={() => setOpenFilterPopoverColId(null)}
                            anchorEl={filterButtonRefs.current[col.id]}
                            langMode={langMode}
                          />
                        )}
                      </th>
                    ))}
                    <th className="w-[220px] min-w-[220px] max-w-[220px] px-2 py-3.5 text-xs font-semibold uppercase text-slate-500 tracking-[0.04em] text-center whitespace-nowrap">
                      {/* No flex-row-reverse: <html dir="rtl"> already lays a
                          flex row out right-to-left, so reversing it again
                          would put the icon back on the LTR side. */}
                      <span className="inline-flex items-center justify-center gap-2">
                        <Download size={14} className="text-slate-500 dark:text-gray-300" />
                        {translations[langMode].pdf}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayedPdfData.length === 0 ? (
                    <tr>
                        <td
                        colSpan={hasPDFDownloadAccess ? 7 : 6}
                        className="py-16 align-middle"
                      >
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3 bg-blue-50 text-blue-600">
                            <Inbox size={22} />
                          </div>
                          <div className="text-sm font-semibold text-slate-800">
                            {translations[langMode].noDatavb}
                          </div>
                          <div className="text-xs mt-1 max-w-xs text-slate-400">
                            {translations[langMode]?.noPdfEntriesHelp ?? "Try adjusting the date range, activity, or account number filters."}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    displayedPdfData.map((item, index) => (
                      <tr
                        key={item.id}
                        className={`text-xs border-b border-slate-100 transition-colors duration-150 ${theme === "dark"
                          ? "dark-mode"
                          : theme === "high-contrast"
                            ? "high-contrast"
                            : "bg-white hover:bg-slate-50/70"
                          }`}
                      >
                        {hasPDFDownloadAccess ? (
                          <td className="w-[60px] min-w-[60px] max-w-[60px] px-0 py-2.5 text-center">
                            <input
                              type="checkbox"
                              checked={selectedAccountNos.has(item.accountNo)}
                              onChange={() => toggleRowSelection(item.accountNo)}
                              className={tableShell.checkbox}
                              aria-label={`Select account ${item.accountNo}`}
                            />
                          </td>
                        ) : null}
                        <td className="w-[60px] min-w-[60px] max-w-[60px] px-0 py-2.5 text-xs text-[#70788f] whitespace-nowrap font-mono text-center">
                          {((currentPage + 1) - 1) * pageSize + index + 1}
                        </td>
                        <td className="px-2 py-2.5 text-xs font-normal text-left rtl:text-right">
                          <span className="rtl-keep-ltr">{item.accountNo}</span>
                        </td>
                        <td className="px-2 py-2.5 text-xs font-normal text-left rtl:text-right">
                          <span className="rtl-keep-ltr">
                            {item.visitDate
                              ? new Date(item.visitDate).toLocaleDateString("en-GB", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                              })
                              : "Invalid date"}
                          </span>
                        </td>
                        <td className="px-2 py-2.5 text-xs font-normal text-left rtl:text-right">
                          <span className="rtl-keep-ltr">
                            {item.visitTime
                              ? new Date(`1970-01-01T${item.visitTime}`).toLocaleTimeString(
                                "en-US",
                                { hour: "2-digit", minute: "2-digit" }
                              )
                              : "Invalid time"}
                          </span>
                        </td>
                        <td className="px-2 py-2.5 text-xs font-normal text-left rtl:text-right">
                          {item.name || "-"}
                        </td>
                        <td className="w-[220px] min-w-[220px] max-w-[220px] px-2 py-2.5 text-xs font-normal text-center">
                          {item.pdfLink ? (
                            <CommonButton colorIntent="primary"
                              variant="primary"
                              type="button"
                              onClick={() => handleExportClick(item.pdfLink)}
                              disabled={!hasPDFDownloadAccess}
                              tooltipText={translations[langMode].downloadpdfbasedonfilters}
                              tooltipPosition="top"
                              className="text-sm font-medium inline-flex items-center justify-center gap-2 shadow-sm min-w-[132px]"
                            >
                              <Download size={16} /> {translations[langMode].download}
                            </CommonButton>
                          ) : (
                            <span className="text-gray-500 dark:text-gray-400">
                              {translations[langMode].nopdfavb}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </TableScrollShell>
            </div>
          </div>
        </div>
      </div>
      {open && hasPDFDownloadAccess ? (
        <Modal pdfLink={selectedPdfLink} setOpen={setOpen} open={open} />
      ) : null}
      {/* No close icon: the single "Ok" button is the only way out, so an X
          would just be a second control doing the same thing. */}
      <AlertModal {...alertProps} showCloseIcon={false} dataTestId="bulk-pdf-export-alert" />
    </div>
  );
}

export default PdfGenerator;
